import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import useAxiosFetch from "../common/useAxiosFetch";
import LoadPage from "../common/LoadPage";
import { Pill, StatusPill } from "./mailingUi";
import { STAGE_TONES } from "./AutomaticMailsTab";

const FILTERS = ['all', 'welcome', 'trialReminder'];

const isWelcome = (item) => item.kind === 'welcome';

const dayLabel = (at, t) => {
  const day = moment(at);
  if (day.isSame(moment(), 'day')) return t('mailing.today');
  if (day.isSame(moment().add(1, 'day'), 'day')) return t('mailing.tomorrow');
  return day.format('dddd D MMMM YYYY');
};

const PlannedTab = () => {
  const { t } = useTranslation();
  const { data, loading, refetch } = useAxiosFetch('/api/mailings/planned');
  const [filter, setFilter] = useState('all');

  const items = data?.items || [];
  const counts = {
    all: items.length,
    welcome: items.filter(isWelcome).length,
    trialReminder: items.filter((item) => !isWelcome(item)).length,
  };

  const groups = useMemo(() => {
    const visible = items.filter((item) => filter === 'all' || (filter === 'welcome') === isWelcome(item));
    const byDay = new Map();
    for (const item of visible) {
      const key = item.due ? 'due' : moment(item.at).format('YYYY-MM-DD');
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(item);
    }
    return [...byDay.entries()];
  }, [data, filter]);

  if (loading && !data) return <LoadPage />;

  return (
    <>
      <div className="mail-toolbar">
        <span className="mail-muted">{t('mailing.planned_sub')}</span>
        <div className="mail-toolbar-spacer" />
        <button type="button" className="mail-link-btn" onClick={refetch}><i className="icon-refresh" /> {t('mailing.refresh')}</button>
      </div>

      {data && !data.schedulerEnabled && (
        <div className="mail-warning"><i className="icon-alert" /> <span>{t('mailing.scheduler_inactive')}</span></div>
      )}

      <div className="mail-chips" role="group" aria-label={t('mailing.filter')}>
        {FILTERS.map((key) => (
          <button key={key} type="button" className={`mail-chip ${filter === key ? 'active' : ''}`} aria-pressed={filter === key} onClick={() => setFilter(key)}>
            {t(`mailing.planned_filter_${key}`)} <span className="mail-chip-count">{counts[key]}</span>
          </button>
        ))}
      </div>

      {groups.length === 0 ? (
        <div className="mail-empty"><i className="icon-calendar" style={{ color: 'var(--muted-text)' }} />{t('mailing.no_planned')}</div>
      ) : (
        <div className="mail-table-wrap">
          <table className="mail-table mail-planned">
            <thead>
              <tr>
                <td>{t('mailing.planned_time')}</td>
                <td>{t('environment')}</td>
                <td>{t('mailing.mailing')}</td>
                <td>{t('mailing.recipients')}</td>
              </tr>
            </thead>
            {groups.map(([day, dayItems]) => (
              <tbody key={day}>
                <tr className="mail-day-row">
                  <td colSpan={4}>
                    {day === 'due' ? t('mailing.planned_due') : dayLabel(dayItems[0].at, t)}
                    <span className="mail-chip-count">{dayItems.length}</span>
                  </td>
                </tr>
                {dayItems.map((item) => {
                  const recipients = item.recipients.filter((recipient) => !recipient.unsubscribed);
                  const skipped = item.recipients.length - recipients.length;
                  return (
                    <tr key={`${item.environment.id}-${item.kind}`}>
                      <td className="nowrap">
                        {item.due ? t('mailing.planned_next_run') : moment(item.at).format('HH:mm')}
                        <span className="mail-sub">{item.due ? t('mailing.planned_within_hour') : moment(item.at).fromNow()}</span>
                      </td>
                      <td>
                        <strong>{item.environment.name}</strong>
                        {item.environment.parentEnvironment !== 'main' && <span className="mail-tag">{item.environment.parentEnvironment}</span>}
                        <span className="mail-sub"><StatusPill status={item.environment.status} /></span>
                      </td>
                      <td>
                        <Pill tone={STAGE_TONES[item.kind]} plain>{t(`mailing.${isWelcome(item) ? 'type_welcome' : 'type_trialReminder'}`)}</Pill>
                        {!isWelcome(item) && <span className="mail-tag">{t(`mailing.stage_${item.kind}`)}</span>}
                        {!item.enabled && <span className="mail-sub mail-off-note">{t('mailing.planned_disabled')}</span>}
                      </td>
                      <td>
                        {recipients.length === 0 && <span className="mail-muted">{t('mailing.no_recipients')}</span>}
                        {recipients.map((recipient) => (
                          <div key={recipient.email} className="mail-recipient" title={recipient.name}>
                            {recipient.email}<span className="mail-tag">{recipient.language.toUpperCase()}</span>
                          </div>
                        ))}
                        {skipped > 0 && (
                          <span className="mail-sub" title={item.recipients.filter((r) => r.unsubscribed).map((r) => r.email).join(', ')}>
                            {t('mailing.planned_skipped', { count: skipped })}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
      )}
      <p className="mail-muted mail-footnote">{t('mailing.planned_note')}</p>
    </>
  );
};

export default PlannedTab;
